import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { FeatureType } from '../../../types';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { dispatchSettingsUpdate } from '../../../helpers/settings';
import featuresInfo from '../../../helpers/state/features-info';
import { isDefined } from '../../../helpers/utils';
import topicData from '../../../helpers/state/topic-data';

@customElement('date-and-time')
export class DateAndTime extends LitElement {

    @query('#hasDates')
    hasDatesElement?: HTMLInputElement;

    @query('#displayOutOfDate')
    displayOutOfDateElement?: HTMLInputElement;

    @state()
    hideDisplayOutOfDate: boolean = false;

	private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    firstUpdated() {
        if(isDefined(this.hasDatesElement)) {
            this.hasDatesElement.checked = topicData.topic?.has_dates ?? false;
            this.hideDisplayOutOfDate = topicData.topic?.has_dates === false;
        }

        if(isDefined(this.displayOutOfDateElement)) {
            this.displayOutOfDateElement.checked = topicData.topic?.should_filter_past_events ?? false;
        }
    }

	toggleHasDates(e: Event) {
        const hasDates = (e.target as HTMLInputElement).checked;

        this.hideDisplayOutOfDate = !hasDates;

        dispatchSettingsUpdate(
            {
				updatedValue: hasDates,
				field: 'has_dates',
            },
            this
        );
	}

    toggleDisplayOutOfDatePoints(e: Event) {
        const displayOutOfDate = (e.target as HTMLInputElement).checked;

        dispatchSettingsUpdate(
            {
				updatedValue: displayOutOfDate,
				field: 'should_filter_past_events',
            },
            this
        );
	}

    openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.DateTime
                },
            })
        );
    }

    render() {
        return html`
            ${featuresInfo.dateTimeEnabled ? html`
                <h4 class="helpSubHeader">Date Element on Points</h4>
                <p>
                    Great for events and more: if you check this box you and your collaborators will
                    be able to add date specificity to added points.
                </p>
                <div class="setting-switch">
                    <label class="switch-label" for="hasDates">No Date/Time</label>
                    <form-switch class="switch-icon" id="hasDates" name="hasDates" @change=${this.toggleHasDates}></form-switch>
                    <label class="switch-label" for="hasDates">Allow Date/Time</label>
                </div>

                <div class="setting-switch ${this.hideDisplayOutOfDate ? 'setting-switch--disabled': ''}">
                    <label class="switch-label" for="displayOutOfDate">Hide out of date points</label>
                    <form-switch class="switch-icon" id="displayOutOfDate" displayOutOfDate="upvote" @change=${this.toggleDisplayOutOfDatePoints}></form-switch>
                    <label class="switch-label" for="displayOutOfDate">Keep out of date points on map</label>
                </div>
            `: html`
                <div class="gatedSection" @click="${this.openUpgradeDrawer}">
                    <div class="gatedField">
                        <img class="menuPng" src="/images/tiers/starter_small.png" />
                        <h4 class="helpSubHeader">Date Element on Points</h4>
                    </div>
                    <p style="margin-top: 0px">
                        Great for events and more: if you check this box you and your collaborators will
                        be able to add date specificity to added points.
                    </p>
                    <div class="setting-switch setting-switch--disabled">
                        <label class="switch-label" for="hasDates">No Date/Time</label>
                        <form-switch class="switch-icon" id="hasDates" name="hasDates" @change=${this.toggleHasDates}></form-switch>
                        <label class="switch-label" for="hasDates">Allow Date/Time</label>
                    </div>

                    <div class="setting-switch setting-switch--disabled">
                        <label class="switch-label" for="displayOutOfDate">Hide out of date points</label>
                        <form-switch class="switch-icon" id="displayOutOfDate" displayOutOfDate="upvote" @change=${this.toggleDisplayOutOfDatePoints}></form-switch>
                        <label class="switch-label" for="displayOutOfDate">Keep out of date points on map</label>
                    </div>
                </div>
            `}
		`;
    }

    static get styles() {
        return css`
            .setting-switch {
                display: grid;
                grid-auto-flow: column;
                grid-auto-columns: 1fr;
                align-items: center;
                justify-content: space-evenly;
                padding-bottom: 12px;
            }

            .setting-switch--disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .switch-label {
                text-align: center;
            }

            .switch-icon {
                margin-left: auto;
                margin-right: auto;
            }

            .gatedField {
                display: flex;
                margin-top: 1rem;
            }

            .gatedSection {
                cursor: pointer;
            }

            .helpSubHeader {
                margin-bottom: 0px;
                font-size: 0.75rem;
                color: var(--theme-input-color);
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: auto;
            }

            @media only screen and (max-width: 550px) {
                .menuPng {
                    margin-top: 20px;
                }
            }
        `;
    }
}
